import {
  ActionIcon,
  Box,
  Button,
  Flex,
  Menu,
  Select,
  TextInput,
  Title,
  Tooltip,
  rem,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { modals } from "@mantine/modals";
import {
  IconActivity,
  IconCaretDown,
  IconEdit,
  IconFileExport,
  IconHandStop,
  IconPlus,
  IconPower,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import * as xlsx from "xlsx";
import { repositoryPos } from "../../../_base/_const/_constVar";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import {
  TAG_API,
  TAG_STATUS,
  TagObjectType,
  TblDmTag,
  TblDmTagCategory,
  readPagedList,
} from "../../../model/TblDmTag";
import { StatusBadge, TagBadge } from "./TagBadges";
import { ConfirmDeleteView } from "./TagDeleteViews";
import TagCreateForm from "./TagCreateForm";
import TagForm from "./TagForm";

const TagList = ({
  objectTypes,
  categoryFilter,
  onCategoryFilterChange,
}: TagListProps) => {
  const headerRef = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<TblDmTag[]>([]);
  const [categories, setCategories] = useState<TblDmTagCategory[]>([]);
  const [rowCount, setRowCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [height, setHeight] = useState(0);
  const [reload, setReload] = useState(false);
  const [keySearch, setKeySearch] = useState("");
  const [debouncedKeySearch] = useDebouncedValue(keySearch, 400);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const selectIds = Object.keys(rowSelection);

  const refresh = () => setReload((prev) => !prev);

  const categoryOptions = useMemo(
    () =>
      objectTypes
        .map((o) => ({
          group: o.name,
          items: categories
            .filter((c) => c.type === o.code)
            .map((c) => ({ value: c.id, label: c.categoryName })),
        }))
        .filter((g) => g.items.length > 0),
    [objectTypes, categories]
  );

  const fetchCategories = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      `${TAG_API.listCategory}?Skip=0&Take=1000`
    );
    if (res?.success) setCategories(readPagedList<TblDmTagCategory>(res.data).list);
  };

  const fetchData = async () => {
    setIsLoading(true);
    setIsRefetching(true);
    setIsError(false);
    try {
      let url = `${TAG_API.listTag}?Skip=${
        pagination.pageIndex * pagination.pageSize
      }&Take=${pagination.pageSize}`;
      if (categoryFilter) url += `&CategoryId=${categoryFilter}`;
      if (debouncedKeySearch.trim())
        url += `&KeySearch=${encodeURIComponent(debouncedKeySearch.trim())}`;

      const res = await repositoryPos.get<MessageResponse<any>>(url);
      if (res?.success) {
        const { list, total } = readPagedList<TblDmTag>(res.data);
        setData(list);
        setRowCount(total);
        setRowSelection({});
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      setData([]);
      setIsError(true);
      console.error(error);
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  };

  const openForm = (tag?: TblDmTag) => {
    // Chỉ cho chọn danh mục đang sử dụng (giữ lại danh mục hiện tại của tag khi sửa)
    const selectable = categories.filter(
      (c) => c.status !== TAG_STATUS.inactive || c.id === tag?.categoryId
    );
    modals.openConfirmModal({
      title: <Title order={5}>{tag ? "Chỉnh sửa tag!" : "Thêm tag!"}</Title>,
      size: "auto",
      children: tag ? (
        <TagForm
          tag={tag}
          categories={selectable}
          objectTypes={objectTypes}
          onSaved={() => {
            refresh();
            fetchCategories();
          }}
        />
      ) : (
        <TagCreateForm
          categories={selectable}
          objectTypes={objectTypes}
          defaultCategoryId={categoryFilter}
          onSaved={() => {
            refresh();
            fetchCategories();
          }}
        />
      ),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  // Đổi trạng thái nhiều tag trong 1 lần gọi API
  const updateStatus = async (tags: TblDmTag[], status: string) => {
    if (tags.length < 1) return;
    const res = await repositoryPos.put<MessageResponse<boolean>>(
      TAG_API.updateTagStatus,
      { ids: tags.map((x) => x.id), status }
    );
    if (res?.success) {
      NotificationExtension.Success("Cập nhật thành công !");
      refresh();
    }
  };

  const deleteSelected = () => {
    if (selectIds.length < 1) {
      NotificationExtension.Warn("Bạn chưa chọn dữ liệu !");
      return;
    }
    modals.openConfirmModal({
      title: <Title order={5}>Xóa tag !</Title>,
      size: "auto",
      children: (
        <ConfirmDeleteView
          message="Bạn có chắc chắn muốn xóa các tag này ?"
          onConfirm={async () => {
            // Xóa nhiều tag trong 1 lần gọi API
            const res = await repositoryPos.delete<MessageResponse<boolean>>(
              TAG_API.deleteTag,
              { ids: selectIds }
            );
            if (res?.success) {
              NotificationExtension.Success("Xóa thành công !");
              refresh();
              fetchCategories();
            }
          }}
        />
      ),
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
    });
  };

  const handleExport = () => {
    try {
      const rows = data.map((x, i) => ({
        STT: pagination.pageIndex * pagination.pageSize + i + 1,
        "Tên tag": x.tagName,
        "Danh mục tag": x.categoryName,
        "Mô tả": x.description,
        "Sử dụng":
          x.status === TAG_STATUS.inactive ? "Dừng sử dụng" : "Đang sử dụng",
      }));
      const worksheet = xlsx.utils.json_to_sheet(rows);
      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Data");
      xlsx.writeFile(workbook, "danh-sach-tag.xlsx");
      toast.success("Export excel thành công", { autoClose: 1500 });
    } catch (error) {
      toast.error("Export excel thất bại", { autoClose: 1500 });
    }
  };

  const columns = useMemo<MRT_ColumnDef<TblDmTag>[]>(
    () => [
      {
        accessorKey: "stt",
        header: "STT",
        size: 10,
        enableSorting: false,
        enableColumnActions: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "tagName",
        header: "Tên tag",
        Cell: ({ row }) => (
          <TagBadge name={row.original.tagName} color={row.original.color} />
        ),
      },
      {
        accessorKey: "categoryName",
        header: "Danh mục tag",
      },
      {
        accessorKey: "description",
        header: "Mô tả",
      },
      {
        accessorKey: "status",
        header: "Sử dụng",
        Cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: "action",
        header: "Thao tác",
        enableSorting: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const active = row.original.status !== TAG_STATUS.inactive;
          return (
            <Box style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Tooltip label="Chỉnh sửa">
                <ActionIcon
                  variant="light"
                  aria-label="Chỉnh sửa"
                  color="orange"
                  onClick={(e) => {
                    e.stopPropagation();
                    openForm(row.original);
                  }}
                >
                  <IconEdit size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label={active ? "Dừng sử dụng" : "Sử dụng"}>
                <ActionIcon
                  variant="light"
                  aria-label={active ? "Dừng sử dụng" : "Sử dụng"}
                  color={active ? "violet" : "green"}
                  onClick={async (e) => {
                    e.stopPropagation();
                    await updateStatus(
                      [row.original],
                      active ? TAG_STATUS.inactive : TAG_STATUS.active
                    );
                  }}
                >
                  <IconPower size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pagination, categories, objectTypes]
  );

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 }
    );
  }, [categoryFilter, debouncedKeySearch]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination, categoryFilter, debouncedKeySearch, reload]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (290 + headerHeight));
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    positionToolbarAlertBanner: "bottom",
    columnFilterDisplayMode: "popover",
    renderTopToolbarCustomActions: () => (
      <div ref={headerRef}>
        <Flex gap="sm" align="flex-end" wrap="wrap">
          <Select
            label="Danh mục tag"
            placeholder="Tất cả danh mục"
            w={240}
            searchable
            clearable
            data={categoryOptions}
            value={categoryFilter}
            onChange={onCategoryFilterChange}
            labelProps={{ style: { fontSize: 12 } }}
          />
          <TextInput
            label="Tìm kiếm"
            placeholder="Tên tag"
            w={220}
            value={keySearch}
            onChange={(e) => setKeySearch(e.currentTarget.value)}
            labelProps={{ style: { fontSize: 12 } }}
          />
        </Flex>
      </div>
    ),
    renderToolbarInternalActions: () => (
      <Flex
        gap="md"
        justify={{ sm: "flex-start", lg: "flex-end" }}
        align="center"
        direction="row"
        wrap="wrap"
      >
        <Button
          leftSection={<IconPlus size={14} />}
          color="blue"
          variant="outline"
          onClick={() => openForm()}
        >
          Thêm tag
        </Button>
        <Button
          leftSection={<IconTrash size={14} />}
          color="red"
          variant="outline"
          onClick={deleteSelected}
          disabled={selectIds.length < 1}
        >
          Xóa (đã chọn)
        </Button>
        <Menu shadow="md" width={200}>
          <Menu.Target>
            <Button
              rightSection={
                <IconCaretDown style={{ width: rem(14), height: rem(14) }} />
              }
            >
              Chức năng
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Application</Menu.Label>
            <Menu.Item
              leftSection={
                <IconFileExport style={{ width: rem(14), height: rem(14) }} />
              }
              onClick={handleExport}
            >
              Export Excel
            </Menu.Item>
            <Menu.Item
              leftSection={
                <IconActivity style={{ width: rem(14), height: rem(14) }} />
              }
              disabled={selectIds.length < 1}
              onClick={() =>
                updateStatus(
                  data.filter((x) => selectIds.includes(x.id)),
                  TAG_STATUS.active
                )
              }
            >
              Sử dụng
            </Menu.Item>
            <Menu.Item
              leftSection={
                <IconHandStop style={{ width: rem(14), height: rem(14) }} />
              }
              disabled={selectIds.length < 1}
              onClick={() =>
                updateStatus(
                  data.filter((x) => selectIds.includes(x.id)),
                  TAG_STATUS.inactive
                )
              }
            >
              Dừng sử dụng
            </Menu.Item>
            <Menu.Item
              leftSection={<IconX style={{ width: rem(14), height: rem(14) }} />}
            >
              Đóng
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Flex>
    ),
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: true,
    getRowId: (row) => row.id,
    initialState: {
      showColumnFilters: false,
      columnVisibility: { id: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    onPaginationChange: setPagination,
    rowCount,
    enableGlobalFilter: false,
    enableColumnFilters: false,
    mantineTableBodyCellProps: () => ({
      style: {
        fontWeight: "500",
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading,
      rowSelection,
    },
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["10", "50", "100"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    mantineTableBodyRowProps: ({ row }) => ({
      onClick: row.getToggleSelectedHandler(),
      style: { cursor: "pointer" },
    }),
  });

  return <MantineReactTable table={table} />;
};

export default TagList;

type TagListProps = {
  objectTypes: TagObjectType[];
  categoryFilter: string | null;
  onCategoryFilterChange: (value: string | null) => void;
};
